/**
 * Farm identity - the farmerId a farm is registered under in the middleware.
 *
 * The middleware's device-list / sensor-data / device-status services return
 * only the records registered under the farmerId sent. The dashboard sends the
 * logged-in user's id, but the middleware's own farm record (API 2 Farm
 * Register, API 14 Get List of Farm) holds the numeric user id of whoever
 * registered the farm (farm 5725 -> farmerId "3614"), and that is the pair a
 * vendor publishes under. So, with a farm selected, every request for that
 * farm goes out under the farm record's farmerId instead.
 *
 * The record is read through innatesMiddlewareService.lookupRecord('farm', id)
 * (GET /api/middleware-lookup). When it finds no record, or the lookup is not
 * available, the caller's fallback (the logged-in user's id) is used, so the
 * records registered from this dashboard stay visible. One lookup per farm per
 * page load, shared by the HAVVA, Avialite and Elliance pages and the home
 * card; invalidateFarmIdentity() forgets it. Only an answer that could not be
 * had (lookup service down, timeout, bad reply) is forgotten by itself after
 * RETRY_UNAVAILABLE_MS, so one failed first visit does not pin the fallback
 * on a farm for the rest of the session.
 *
 * A record flagged deleted is used as it is (api/middleware-lookup already
 * prefers a record that is not deleted): it still names the user who
 * registered the farm. There is no client-side time limit on the lookup; the
 * server function aborts its upstream read after 20 s and answers 502, which
 * counts as "not available".
 */

import innatesMiddlewareService from './innatesMiddlewareService';

// How long a lookup that was NOT available stands in for a farm before the
// next resolve asks again. Definite answers are kept for the page load.
const RETRY_UNAVAILABLE_MS = 30 * 1000;

// Same rule as the three services: empty, 'all' and 'ALL' mean every farm
const isAllFarms = (farmId) =>
  farmId === null || farmId === undefined || farmId === '' || farmId === 'all' || farmId === 'ALL';

// farmId -> Promise<string|null>: the farm record's farmerId, or null when no
// record was found or the lookup was not available. Never rejects.
const lookups = new Map();
// farmId -> string|null, once the lookup has answered (for peekFarmerId)
const known = new Map();

// The farmerId of a lookup reply, or null when it holds none
const toFarmerId = (record) => {
  if (!record || record.found !== true) return null;
  const farmerId = record.farmerId === null || record.farmerId === undefined ? '' : String(record.farmerId).trim();
  return farmerId === '' ? null : farmerId;
};

/**
 * The farmerId the middleware's farm record holds for a farm, or null when
 * there is no record / the lookup is not available. The answer is cached for
 * the session; a lookup already running for the farm is shared.
 *
 * @param {string|number|null|undefined} farmId - A farm id; empty / 'all' / 'ALL' give null
 * @returns {Promise<string|null>}
 */
export const lookupRegisteredFarmerId = (farmId) => {
  if (isAllFarms(farmId)) return Promise.resolve(null);
  const key = String(farmId);
  let lookup = lookups.get(key);
  if (!lookup) {
    lookup = innatesMiddlewareService.lookupRecord('farm', key)
      .catch(() => null)
      .then((record) => {
        const farmerId = toFarmerId(record);
        // Not overtaken by invalidateFarmIdentity() meanwhile
        if (lookups.get(key) === lookup) {
          if (record === null || record === undefined) {
            // Lookup not available: fall back for now, ask again a little later
            setTimeout(() => {
              if (lookups.get(key) === lookup) lookups.delete(key);
            }, RETRY_UNAVAILABLE_MS);
          } else {
            known.set(key, farmerId);
          }
        }
        return farmerId;
      });
    lookups.set(key, lookup);
  }
  return lookup;
};

/**
 * The farmerId to send for a farm: its registered farmerId when the middleware
 * has a farm record, else fallbackUserId (today's behaviour). For "all farms"
 * (empty / 'all' / 'ALL') always fallbackUserId. Never rejects.
 *
 * @param {string|number|null|undefined} farmId
 * @param {string|null|undefined} fallbackUserId - Usually the logged-in user's id
 * @returns {Promise<string|null|undefined>}
 */
export const resolveFarmerId = async (farmId, fallbackUserId) => {
  const registered = await lookupRegisteredFarmerId(farmId);
  return registered || fallbackUserId;
};

/**
 * Same as resolveFarmerId(), without waiting: the registered farmerId when its
 * lookup has already answered, else fallbackUserId.
 */
export const peekFarmerId = (farmId, fallbackUserId) => {
  if (isAllFarms(farmId)) return fallbackUserId;
  return known.get(String(farmId)) || fallbackUserId;
};

/**
 * Forget the cached lookup of one farm (or of every farm with no argument),
 * e.g. after the farm's middleware record was created or changed, so the next
 * resolveFarmerId() asks again.
 *
 * @param {string|number|null} farmId - null = every farm
 */
export const invalidateFarmIdentity = (farmId = null) => {
  if (farmId === null || farmId === undefined) {
    lookups.clear();
    known.clear();
    return;
  }
  lookups.delete(String(farmId));
  known.delete(String(farmId));
};
